import { useDebounceFn } from 'ahooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { v4 as uuidv4 } from 'uuid';
import { Input, Popover } from '@meitu/saas-ui';
import Angle from '../../../color-angle';
import GradientInteractive, { IColor } from '../../../color-gradient';
import Saturation from '../../../color-saturation';
import EyeDropper from '../../../color-sketch-picker/eye-dropper';
import { useLocaleStore } from '../../../i18n';
import { color as handleColor, HsvaColor, hsvaToHex, rgbaToHsva } from '../../../utils/color-convert';
import './index.less';
import EditableInput from '../../../color-editable-input';
import EditableInputRGBA from '../../../color-editable-input-rgba';
import useRecentlyUsedColors from '../../../useRecentlyUsedColors';
import HexInput from './hex-input';
import ToolGroup from './tool-group';

const GradientInteractiveWidth = 164;
const PRESET_COLORS = [
    'linear-gradient(90deg, #ff6e7fff 0%, #bfe9ffff 100%)',
    'linear-gradient(90deg, #e65c00ff 0%, #f9d423ff 100%)',
    'linear-gradient(90deg, #ec008cff 0%, #fc6767ff 100%)',
    'linear-gradient(90deg, #00467fff 0%, #a5cc82ff 100%)',
    'linear-gradient(90deg, #076585ff 0%, #ffffffff 100%)',
    'linear-gradient(90deg, #9796f0ff 0%, #fbc7d4ff 100%)',
    'linear-gradient(90deg, #5433ffff 0%, #20bdffff 50%,#a5fecbff 100%)',
    'linear-gradient(90deg, #00416aff 0%, #799f0cff 50%,#ffe000ff 100%)',
    'linear-gradient(90deg, #f7f8f8ff 0%, #acbb78ff 100%)',
    'linear-gradient(90deg, #ffe000ff 0%, #799f0cff 100%)',
    'linear-gradient(90deg, #bdc3c7ff 0%, #2c3e50ff 100%)',
    'linear-gradient(90deg, #ee9ca7ff 0%, #ffdde1ff 100%)',
    'linear-gradient(90deg, #c6ffddff 0%, #fbd786ff 50%,#f7797dff 100%)',
    'linear-gradient(90deg, #12c2e9ff 0%, #c471edff 50%,#f64f59ff 100%)',
    'linear-gradient(90deg, #1f4037ff 0%, #99f2c8ff 100%)',
    'linear-gradient(90deg, #c31432ff 0%, #240b36ff 100%)',
    'linear-gradient(90deg, #659999ff 0%, #f4791fff 100%)',
    'linear-gradient(90deg, #dd3e54ff 0%, #6be585ff 100%)',
    'linear-gradient(90deg, #8a2387ff 0%, #e94057ff 50%,#f27121ff 100%)',
    'linear-gradient(90deg, #1e9600ff 0%, #fff200ff 50%,#ff0000ff 100%)',
    'linear-gradient(90deg, #59c173ff 0%, #a17fe0ff 50%,#5d26c1ff 100%)',
    'linear-gradient(90deg, #40e0d0ff 0%, #ff8c00ff 50%,#ff0080ff 100%)',
    'linear-gradient(90deg, #00f260ff 0%, #0575e6ff 100%)',
    'linear-gradient(90deg, #03001eff 0%, #7303c0ff 33%,#ec38bcff 66%,#fdeff9ff 100%)',
    'linear-gradient(90deg, #ada996ff 0%, #f2f2f2ff 33%,#dbdbdbff 66%,#eaeaeaff 100%)',
    'linear-gradient(90deg, #1a2a6cff 0%, #b21f1fff 50%,#fdbb2dff 100%)',
    'linear-gradient(90deg, #ef3b36ff 0%, #ffffffff 100%)',
    'linear-gradient(90deg, #20002cff 0%, #cbb4d4ff 100%)',
    'linear-gradient(90deg, #e8cbc0ff 0%, #636fa4ff 100%)',
    'linear-gradient(90deg, #a770efff 0%, #cf8bf3ff 50%,#fdb99bff 100%)',
    'linear-gradient(90deg, #fffc00ff 0%, #ffffffff 100%)',
    'linear-gradient(90deg, #a80077ff 0%, #66ff00ff 100%)',
    'linear-gradient(90deg, #f7ff00ff 0%, #db36a4ff 100%)',
    'linear-gradient(90deg, #bbd2c5ff 0%, #536976ff 50%,#292e49ff 100%)',
    'linear-gradient(90deg, #f0c27bff 0%, #4b1248ff 100%)',
    'linear-gradient(90deg, #000046ff 0%, #1cb5e0ff 100%)',
];

const ua = navigator.userAgent.toLowerCase();
const isPC = ua.indexOf('mtxx-pc') > -1;
const isMac = ua.indexOf('mtxx-mac') > -1;
const isClient = isPC || isMac;
// Windows 下 Chromium 内核 == 150 版本使用 EyeDropper 取色器会导致浏览器卡死，禁用该功能
const chromeVersionMatch = navigator.userAgent.match(/Chrom(?:e|ium)\/(\d+)/);
const chromeVersion = chromeVersionMatch ? parseInt(chromeVersionMatch[1], 10) : 0;
const isWindows = /windows/i.test(navigator.userAgent);
const isChromiumEyeDropperBroken = isWindows && chromeVersion === 150;

export interface GradientProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'color'> {
    prefixCls?: string;
    width?: number;
    gradient?: string;
    presetColors?: string[];
    useRGBAInput?: boolean;
    editableDisable?: boolean;
    onChange?: (newShade: string) => void;
    onChangeComplete?: (newShade: string) => void;
}
interface ColorItem {
    color: string;
    value: number;
    left: string;
    top: string;
    x: number;
    id: string;
}

const Gradient = React.forwardRef<HTMLDivElement, GradientProps>((props, ref) => {
    const {
        prefixCls = 'c-color-gradient',
        className,
        onChange,
        onChangeComplete,
        width = 280,
        useRGBAInput = false,
        presetColors = PRESET_COLORS,
        editableDisable = true,
        style,
        // 默认渐变
        gradient,
        ...other
    } = props;
    const { $t } = useLocaleStore();
    const lastChangeShadeRef = useRef<string>('');
    const [hsva, setHsva] = useState({ h: 209, s: 36, v: 90, a: 1 });
    const [hex, setHex] = useState('');
    const [colors, setColors] = useState<IColor[]>([]);
    const [active, setActive] = useState<string>('');
    const [alpha, setAlpha] = useState(0);
    const [angle, setAngle] = useState<number>(0);
    const [inputAngle, setInputAngle] = useState<string>('0');
    const hasEyeDropper = isClient || isChromiumEyeDropperBroken ? false : 'EyeDropper' in window ? true : false;
    const recentlyUsedColorsController = useRecentlyUsedColors('gradient');
    // 初始化的时候就冻结起来, 期间新增颜色都不要同步到列表中
    const recentlyUsedColors = useMemo(() => recentlyUsedColorsController.colors, []);

    const { run: changeAfter } = useDebounceFn(
        (newShade: string) => {
            lastChangeShadeRef.current = newShade;
            onChangeComplete?.(newShade);
        },
        {
            wait: 300,
        },
    );

    const init = (str: string) => {
        let list: ColorItem[] = [];
        if (str.includes('rgba')) {
            list =
                str?.match(/rgba.*?\%/g)?.map((_str) => {
                    const [r, g, b, a, left] = _str.match(/\d{1,3}/g)!;
                    return {
                        color: `rgba(${r}, ${g}, ${b}, ${a})`,
                        value: Number(left) / 100,
                        left: left + '%',
                        top: '0',
                        x: (GradientInteractiveWidth * Number(left)) / 100,
                        id: uuidv4(),
                    };
                }) || [];
        } else if (str.includes('#')) {
            list =
                str?.match(/#.*?%/g)?.map((_str) => {
                    const color = (_str.match(/#\w{6,8}/g) || [''])[0];
                    const left = Number((_str.match(/\s.*[%]$/) || ['0%'])[0].replace('%', ''));
                    return {
                        color,
                        value: Number(left) / 100,
                        left: left + '%',
                        top: '0',
                        x: (GradientInteractiveWidth * Number(left)) / 100,
                        id: uuidv4(),
                    };
                }) || [];
        }
        let angle = Number((str?.match(/(-?\d+)deg/) || ['', '90'])[1]);
        if (angle >= 90) {
            angle = -(angle - 90);
        } else if (angle <= 0) {
            angle = Math.abs(angle) + 90;
        } else {
            angle = 90 - angle;
        }
        // angle = angle >= 90 ? -(angle - 90) : Math.abs(angle) + 90;
        setInputAngle(angle.toString());
        if (angle < 0) {
            setAngle(Math.abs(angle));
        } else {
            setAngle(360 - angle);
        }
        // setAngle(angle);
        // setInputAngle(beforeAngle(angle).toString());
        // setAngle(angle);
        setColors(list!);
        setActive(list![0].id);
    };
    useEffect(() => {
        // 取出渐变色数组
        gradient && init(gradient);
    }, []);
    useEffect(() => {
        if (active) {
            const { color } = colors.find((c) => c.id === active)!;
            if (color.includes('rgba')) {
                const [r, g, b, a = 1] = color.match(/\d+(\.\d+)?/g)!.map(Number);
                const hsva = rgbaToHsva({ r, g, b, a });
                const { hex } = handleColor(hsva);
                setHex(hex.slice(1, 7));
                setAlpha(Math.ceil(a * 100));
                setHsva(hsva);
            } else {
                const {
                    hex,
                    hsva: { a },
                    hsva,
                } = handleColor(color);
                setHex(hex.slice(1, 7));
                if (/00$/gi.test(color)) {
                    setAlpha(0);
                    hsva.a = 0;
                } else {
                    setAlpha(Math.ceil(a * 100));
                }
                setHsva(hsva);
            }
        }
    }, [active]);
    const handleChange = useCallback(
        (hsv: HsvaColor) => {
            const {
                hex,
                hexa,
                hsva: { a },
                rgba,
            } = handleColor(hsv);
            setHex(hex.slice(1, 7));
            setAlpha(Math.ceil(a * 100));
            setHsva(hsv);
            const index = colors.findIndex((c) => c.id === active)!;
            colors[index] = {
                ...colors[index],
                color: hexa,
            };
            setColors([...colors]);
        },
        [hsva, colors],
    );
    useEffect(() => {
        let backgroundImage = '';
        colors
            ?.sort((a, b) => a.value - b.value)
            .forEach((item, index) => {
                const { color, left } = item;
                backgroundImage += `${color} ${left}${index === colors.length - 1 ? '' : ','}`;
            });
        const str = `linear-gradient(${angle <= 180 ? 90 + angle : -270 + angle}deg, ${backgroundImage})`;
        if (gradient !== str && colors.length > 0 && onChange && onChangeComplete) {
            onChange(str);
            changeAfter(str);
        }
    }, [colors, angle]);
    const beforeAngle = (angle) => {
        let _angle = 0;
        if (angle >= 0 && angle <= 180) {
            _angle = -angle;
        } else {
            _angle = -angle + 360;
        }
        return _angle;
    };

    useEffect(() => {
        return () => {
            if (lastChangeShadeRef.current) {
                recentlyUsedColorsController.addColor(lastChangeShadeRef.current);
                lastChangeShadeRef.current = '';
            }
        };
    }, []);
    return (
        <div
            {...other}
            className={`${prefixCls} ${className || ''}`}
            ref={ref}
            style={{
                width,
                ...style,
            }}
        >
            <div className="color-gradient-select">
                <GradientInteractive {...{ colors, setColors, active, setActive, width: GradientInteractiveWidth }} />
                <Popover
                    zIndex={2001}
                    placement="bottom"
                    overlayClassName="c-color-angle-wrap"
                    getPopupContainer={(triggerNode) => {
                        return triggerNode?.parentNode as HTMLElement;
                    }}
                    content={() => {
                        return (
                            <Angle
                                angle={angle}
                                onChange={(val) => {
                                    setAngle(val);
                                    setInputAngle(beforeAngle(val).toString());
                                }}
                            />
                        );
                    }}
                >
                    <Input
                        value={inputAngle}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            //   console.log('Change:', e.target.value);
                            setInputAngle(e.target.value);
                        }}
                        onBlur={(e) => {
                            console.log('onBlur:', e.target.value);
                            let val = Number((e.target.value.match(/-?\d+/) || ['0'])[0]);
                            if (val > 180 || val < -180) {
                                val = 0;
                            }
                            setInputAngle(val.toString());
                            if (val < 0) {
                                setAngle(Math.abs(val));
                            } else {
                                setAngle(360 - val);
                            }
                        }}
                    />
                </Popover>
            </div>
            <Saturation
                hsva={{ ...hsva, a: 1 }}
                style={{ width: 'auto', height: 160, margin: '0 -16px' }}
                // 如果透明度为0，设置颜色, 需要变成1
                onChange={(newColor) => handleChange({ ...newColor, a: hsva.a === 0 ? 1 : hsva.a })}
            />
            <ToolGroup
                useRGBAInput={useRGBAInput}
                hsva={hsva}
                hasEyeDropper={hasEyeDropper}
                handleChange={handleChange}
                setHex={setHex}
                setHsva={setHsva}
            />
            <div className="c-color-sketch-picker__menu">
                {hasEyeDropper && !useRGBAInput ? (
                    <EyeDropper setHex={setHex} setHsva={setHsva} handleChange={handleChange} />
                ) : null}
                {useRGBAInput ? (
                    <div className="c-color-sketch-picker__menu--inputRGBA">
                        <EditableInput
                            label="Hex"
                            prefix="#"
                            value={hex}
                            style={{ width: 68 }}
                            onChange={(e) => {
                                const result = handleColor(e.target.value);
                                if (result.hex) {
                                    setHex(result.hex.slice(1, 7));
                                    setHsva({ ...result.hsva, a: alpha / 100 });
                                    handleChange({ ...result.hsva, a: alpha / 100 });
                                } else {
                                    setHex(hsvaToHex(hsva).slice(1, 7));
                                }
                            }}
                        />
                        <EditableInputRGBA
                            hsva={hsva}
                            onChange={(color) => {
                                handleChange(color.hsva);
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <HexInput
                            hasEyeDropper={hasEyeDropper}
                            hex={hex}
                            hsva={hsva}
                            setHex={setHex}
                            setHsva={setHsva}
                            handleChange={handleChange}
                            alpha={alpha}
                        />
                        <div className={`c-color-sketch-picker__menu--input ${hasEyeDropper ? '' : 'w120'}`}>
                            <Input
                                suffix="%"
                                value={alpha}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const { target } = e;
                                    const { value } = target;
                                    let _alpha = +value;
                                    if (_alpha > 100) _alpha = 100;
                                    if (_alpha < 0) _alpha = 0;
                                    // 如果是NaN不好判断
                                    setAlpha(_alpha === 0 ? 0 : _alpha || alpha);
                                }}
                                onBlur={() => {
                                    handleChange({ ...hsva, a: alpha / 100 });
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="c-color-gradient-picker__expand">
                {recentlyUsedColors.length > 0 && (
                    <>
                        <div className="c-color-sketch-picker__expand--title" style={{ marginTop: 4 }}>
                            {$t((v) => v.Title_RecentlyUsed)}
                        </div>
                        <div className="c-color-gradient-picker__expand--list" style={{ height: 'max-content' }}>
                            {recentlyUsedColors.map((color) => (
                                <div
                                    key={color}
                                    className="c-color-gradient-picker__expand--item"
                                    style={{ backgroundImage: color, border: 'none' }}
                                    onClick={() => {
                                        init(color);
                                    }}
                                ></div>
                            ))}
                        </div>
                    </>
                )}
                <div className="c-color-gradient-picker__expand--title">{$t((v) => v.Title_PresetGradient)}</div>
                <div className="c-color-gradient-picker__expand--list">
                    <Scrollbars>
                        {presetColors.map((color, index: number) => {
                            return (
                                <div
                                    className="c-color-gradient-picker__expand--item"
                                    style={{ backgroundImage: color as string, border: 'none' }}
                                    key={index}
                                    onClick={() => {
                                        init(color);
                                    }}
                                ></div>
                            );
                        })}
                    </Scrollbars>
                </div>
            </div>
        </div>
    );
});

Gradient.displayName = 'Gradient';

export default React.memo(Gradient);
